import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "./Courses.css";
import { getProducts, trackCourseSearch } from "../../api/productsApi";
import ProposalModal from "../../components/ProposalModal/ProposalModal";
import { getCourseSlug, normalizeText } from "../../utils/slugUtils";
import { stripHtml } from "../../utils/textUtils";

function getPageSize() {
  if (typeof window === "undefined") return 6;

  const width = window.innerWidth;

  if (width >= 1800) return 20;
  if (width >= 1200) return 16;
  if (width >= 969) return 9;
  if (width >= 601) return 8;

  return 6;
}

export default function Courses() {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const searchTerm = query.get("search") || "";
  const currentPage = Math.max(1, Number(query.get("page")) || 1);

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [courses, setCourses] = useState([]);
  const [totalCourses, setTotalCourses] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [proposalCourse, setProposalCourse] = useState(null);
  const [pageSize, setPageSize] = useState(getPageSize);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const handleResize = () => {
      const nextPageSize = getPageSize();

      setPageSize((currentPageSize) =>
        currentPageSize === nextPageSize ? currentPageSize : nextPageSize
      );
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    let live = true;
    setIsLoading(true);

    getProducts(currentPage, pageSize, searchTerm)
      .then((result) => {
        if (!live) return;
        const items = result?.data || result?.courses || result?.products || [];
        const meta = result?.pagination || result || {};
        setCourses(items);
        setTotalCourses(meta.totalProducts ?? meta.totalCourses ?? items.length);
        setTotalPages(meta.totalPages ?? 1);
        if (searchTerm && items.length) {
          trackCourseSearch(items.map((item) => item._id).filter(Boolean)).catch(() => {});
        }
      })
      .catch(() => {
        if (!live) return;
        setCourses([]);
        setTotalCourses(0);
        setTotalPages(1);
      })
      .finally(() => {
        if (live) setIsLoading(false);
      });

    return () => {
      live = false;
    };
  }, [currentPage, pageSize, searchTerm]);

  const runSearch = (term = searchInput, page = 1) => {
    const value = term.trim();
    navigate(value ? `/courses?search=${encodeURIComponent(value)}&page=${page}` : `/courses?page=${page}`);
  };

  const hasCourses = courses.length > 0;

  return (
    <>
      <section className="course-search-results" id="courses" aria-labelledby="course-search-title">
        <div className="course-search-header">
          <h2 id="course-search-title">Find a Training Course</h2>
          <p>Search our training courses by title, technology, skill, or topic.</p>
          <form className="results-search" onSubmit={(event) => { event.preventDefault(); runSearch(); }} role="search">
            <label htmlFor="course-search-input" className="sr-only">Search training courses</label>
            <input id="course-search-input" type="search" placeholder="Search courses, technologies, or skills..." value={searchInput} disabled={isLoading} onChange={(event) => setSearchInput(event.target.value)} autoComplete="off" />
            <button type="submit" disabled={isLoading} aria-label="Search training courses">{isLoading ? "Searching..." : "Search"}</button>
          </form>
        </div>

        {!isLoading && (
          <div className="search-result-summary" aria-live="polite">
            {searchTerm ? <><strong>{totalCourses}</strong> {totalCourses === 1 ? "course" : "courses"} found for <strong>“{searchTerm}”</strong></> : <>Showing <strong>{totalCourses}</strong> available {totalCourses === 1 ? "course" : "courses"}.</>}
          </div>
        )}

        {isLoading && <div className="search-loading" role="status" aria-live="polite"><span className="search-loading-spinner" /><span>Loading training courses...</span></div>}

        {!isLoading && !hasCourses && (
          <div className="search-empty-state">
            <h3>{searchTerm ? "No matching courses" : "No courses available"}</h3>
            <p>{searchTerm ? <>We couldn't find a course matching <strong>“{searchTerm}”</strong>.</> : "No course records are available right now."}</p>
          </div>
        )}

        {!isLoading && hasCourses && (
          <div className="course-grid" aria-label="Course search results">
            {courses.map((course) => {
              const courseTitle = normalizeText(course.post_title || course.title || "Course");
              const cleanDescription = normalizeText(stripHtml(course.post_content || ""));
              const description = cleanDescription.length > 160 ? `${cleanDescription.substring(0, 160)}...` : cleanDescription;
              return (
                <article key={course._id} className="course-card">
                  <h3>{courseTitle}</h3>
                  {description && <p>{description}</p>}
                  <div className="course-card-actions">
                    <Link to={`/courses/${getCourseSlug(course)}`} className="course-details-button" aria-label={`View details for ${courseTitle}`}>View Course</Link>
                    <button type="button" className="quote-button" onClick={() => setProposalCourse(course)}>Request Quote</button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!isLoading && hasCourses && totalPages > 1 && (
          <nav className="pagination" aria-label="Course search pagination">
            <button type="button" disabled={currentPage <= 1} onClick={() => runSearch(searchTerm, currentPage - 1)}>Previous</button>
            <span aria-current="page">Page {currentPage} of {totalPages}</span>
            <button type="button" disabled={currentPage >= totalPages} onClick={() => runSearch(searchTerm, currentPage + 1)}>Next</button>
          </nav>
        )}
      </section>
      <ProposalModal isOpen={proposalCourse !== null} selectedCourse={proposalCourse} onClose={() => setProposalCourse(null)} />
    </>
  );
}
